import type { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-pro";

type RowLike = { id?: unknown; uuid?: unknown; isDummyRow?: boolean };

function rowKey(row: RowLike): GridRowId {
	return (row.id != null ? row.id : row.uuid) as GridRowId;
}

/** Default `rowSelectionModel` for MUI X Data Grid Pro (v7+ uses `ids` as a `Set`). */
export function emptyGridRowSelectionModel(): GridRowSelectionModel {
	return { type: "include", ids: new Set<GridRowId>() };
}

function dataRows(rows?: RowLike[]) {
	return rows?.filter((r) => !r?.isDummyRow) ?? [];
}

/** Count of selected rows (supports `include` and `exclude` selection models). */
export function gridRowSelectionSize(
	model: GridRowSelectionModel | undefined,
	rows?: RowLike[],
): number {
	if (!model) return 0;
	if (model.type === "include") {
		return model.ids.size;
	}
	const list = dataRows(rows);
	if (list.length === 0) return 0;
	return list.filter((r) => !model.ids.has(rowKey(r))).length;
}

/** Resolve concrete selected row ids for include and exclude selection models. */
export function resolveGridSelectedRowIds(
	model: GridRowSelectionModel | undefined,
	rows?: RowLike[],
): GridRowId[] {
	const list = dataRows(rows);
	if (!model || list.length === 0) return [];

	if (model.type === "exclude") {
		return list
			.filter((row) => !model.ids.has(rowKey(row)))
			.map(rowKey);
	}

	return list
		.filter((row) => model.ids.has(rowKey(row)))
		.map(rowKey);
}

/** Selected row ids as an array (for APIs and `.includes` / `.map` call sites). */
export function gridRowSelectionIdsArray(
	model: GridRowSelectionModel | undefined,
	rows?: RowLike[],
): GridRowId[] {
	if (!model) return [];
	if (model.type === "include") {
		return Array.from(model.ids);
	}
	const list = dataRows(rows);
	return list.filter((r) => !model.ids.has(rowKey(r))).map(rowKey);
}
